import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Alert,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  fetchDeviceTrackingSessions,
  getUniqueUserIds,
  DeviceTrackingSession
} from '../services/deviceTrackingService';

export default function DeviceReporting() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const currentDate = new Date();
  const currentPeriod = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  const [billingPeriod, setBillingPeriod] = useState(currentPeriod);
  const [selectedFlavor, setSelectedFlavor] = useState<string>('satlog');

  // Data states
  const [sessions, setSessions] = useState<DeviceTrackingSession[]>([]);
  const [uniqueUserIds, setUniqueUserIds] = useState<string[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Product flavor options - only satlog for now
  const flavorOptions = [
    { value: 'satlog', label: 'Satlog' },
  ];

  const handleBillingPeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBillingPeriod(event.target.value);
  };

  const handleFlavorChange = (event: SelectChangeEvent<string>) => {
    setSelectedFlavor(event.target.value);
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      setError(null);
      setHasSearched(true);

      // Validate YYYY-MM format
      const [year, month] = billingPeriod.split('-').map(Number);

      if (!year || !month || month < 1 || month > 12) {
        setError('Invalid billing period format. Please use YYYY-MM (e.g., 2025-10)');
        setLoading(false);
        return;
      }

      // Fetch sessions using the billingPeriod string directly
      const fetchedSessions = await fetchDeviceTrackingSessions(
        billingPeriod,
        selectedFlavor
      );

      setSessions(fetchedSessions);
      const uniqueIds = getUniqueUserIds(fetchedSessions);
      setUniqueUserIds(uniqueIds);

    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  // Get month name for display
  const getMonthName = (monthNum: number) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[monthNum - 1] || '';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Device Reporting
      </Typography>

      {/* Filter Controls */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Billing Period & Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel shrink>Billing Period (YYYY-MM)</InputLabel>
              <input
                type="month"
                value={billingPeriod}
                onChange={handleBillingPeriodChange}
                style={{
                  width: '100%',
                  padding: '16.5px 14px',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px',
                  marginTop: '16px'
                }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Product Flavor</InputLabel>
              <Select
                value={selectedFlavor}
                label="Product Flavor"
                onChange={handleFlavorChange}
              >
                {flavorOptions.map(flavor => (
                  <MenuItem key={flavor.value} value={flavor.value}>
                    {flavor.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AssessmentIcon />}
              onClick={handleGenerateReport}
              disabled={loading}
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading Indicator */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {/* Results */}
      {!loading && hasSearched && (
        <>
          {/* Summary Card */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Billing Period
                  </Typography>
                  <Typography variant="h5" component="div">
                    {(() => {
                      const [year, month] = billingPeriod.split('-').map(Number);
                      return `${getMonthName(month)} ${year}`;
                    })()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {billingPeriod}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Product Flavor
                  </Typography>
                  <Typography variant="h5" component="div">
                    {flavorOptions.find(f => f.value === selectedFlavor)?.label || 'Satlog'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ bgcolor: 'primary.main', color: 'white' }}>
                <CardContent>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom>
                    Total Unique Users
                  </Typography>
                  <Typography variant="h4" component="div">
                    {uniqueUserIds.length}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {sessions.length} total sessions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* User ID List Table */}
          <Card elevation={3}>
            <CardHeader
              title="Unique User IDs"
              subheader={`${uniqueUserIds.length} unique users in ${(() => {
                const [year, month] = billingPeriod.split('-').map(Number);
                return `${getMonthName(month)} ${year}`;
              })()}`}
            />
            <Divider />
            <CardContent>
              {uniqueUserIds.length === 0 ? (
                <Alert severity="info">
                  No users found for the selected period and filters.
                </Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>User ID</strong></TableCell>
                        <TableCell align="right"><strong>Session Count</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uniqueUserIds.map((userId, index) => {
                        const userSessions = sessions.filter(s => s.userId === userId);
                        const sessionCount = userSessions.length;

                        return (
                          <TableRow
                            key={userId}
                            hover
                            sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
                          >
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {userId}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={sessionCount}
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Initial State Message */}
      {!loading && !hasSearched && (
        <Paper elevation={3} sx={{ p: 5, textAlign: 'center' }}>
          <AssessmentIcon sx={{ fontSize: 80, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Select a billing period and generate a report
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Choose the billing period (YYYY-MM format) and product flavor above, then click "Generate Report" to view device tracking data.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
